/* eslint-disable prettier/prettier */
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { Link, matchPath, useLocation, useNavigate } from 'react-router-dom';

// material-ui
import { alpha, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Avatar from '@mui/material/Avatar';
import ButtonBase from '@mui/material/ButtonBase';
import Chip from '@mui/material/Chip';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

// project imports
import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';
import { MenuOrientation, ThemeDirection, ThemeMode } from 'config';
import useConfig from 'hooks/useConfig';

// third party
import { FormattedMessage } from 'react-intl';

// assets
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { useOrganization } from 'contexts/OrganizationContext';
import { useCloudRestApiAccess } from 'contexts/CloudRestApiAccessContext';
import { isFeatureEnabled, isFeatureListLoaded } from 'utils/supportedFeatures';
import useIcmEligibility from 'hooks/useIcmEligibility';
import { closeAllDrawers } from '../../../../contexts/AddDrawerContext';
import { getRumNavId, getRumNavScope, recordRumNavClick } from '../../../../rum/rumAnalytics';
import { getRumFeatureKey, getRumPageKey } from '../../../../rum/rumPageId';


export default function NavItem({ item, level, isParents = false, setSelectedID, tooltipMessage }) {
  const theme = useTheme();
  const downMD = useMediaQuery(theme.breakpoints.down('md'));
  const ref = useRef(null);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { mode, menuOrientation, borderRadius, themeDirection } = useConfig();

  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downMD;
  const { updateOrganizationContext, setScopeLevel, orgId, scopeId, currentNetwork} = useOrganization();
  const { loading: featureFlagsLoading } = useCloudRestApiAccess();
  
  
  // Generate dynamic URL for Organization-wide menu item
  const getItemUrl = () => {
    if (item.id === 'Organization-wide') {
      const selectedType = localStorage.getItem('selectedType') 
        ? JSON.parse(localStorage.getItem('selectedType')) 
        : null;
      
      console.log('Organization-wide clicked - Debug info:', {
        selectedType,
        savedOrgName: localStorage.getItem('savedOrgName'),
        orgId: localStorage.getItem('OrgId'),
        currentPath: window.location.pathname
      });
      
      // If already at organization level
      if (selectedType && selectedType.label && selectedType.type === 'Organization-wide') {
        console.log('Already at organization level, using selectedType.label:', selectedType.label);
        return `/organization/${encodeURIComponent(selectedType.label)}/dashboard`;
      }
      
      // Try to get saved organization name first (this should work after our NavGroup fix)
      const savedOrgName = localStorage.getItem('savedOrgName');
      if (savedOrgName) {
        console.log('Using savedOrgName:', savedOrgName);
        return `/organization/${encodeURIComponent(savedOrgName)}/dashboard`;
      }
      
      // If at location level or other cases, extract from current URL if available
      const pathParts = window.location.pathname.split('/');
      if (pathParts.includes('organization') && pathParts.length > pathParts.indexOf('organization') + 1) {
        const orgName = decodeURIComponent(pathParts[pathParts.indexOf('organization') + 1]);
        console.log('Extracted orgName from URL:', orgName);
        return `/organization/${encodeURIComponent(orgName)}/dashboard`;
      }
      
      // If all else fails, we need a default organization - this should not happen in normal flow
      console.warn('Could not determine organization for Organization-wide navigation');
      return '/mspHome/dashboard'; // Fallback to MSP home
    }
    return item?.link ? item.link : item.url;
  };
  
  const itemUrl = getItemUrl();
  const isAlarmsItem = Boolean(item?.id) && String(item.id).includes('Alarms');
  
  // Check if item is selected - for Organization-wide, check if we're anywhere in that organization
  const getIsSelected = () => {
    if (item.id === 'Organization-wide') {
      return pathname.startsWith('/organization/');
    }
    // Special case: Highlight "Devices" menu when viewing device detail pages
    // Device detail routes are at /devices/... (e.g., /devices/MHS/summary) but the Devices menu is at /locationDevice
    if (item.id === 'LocDevices-loc' && pathname.startsWith('/devices/')) {
      return true;
    }
    return !!matchPath({ path: itemUrl, end: false }, pathname);
  };
  
  const isSelected = getIsSelected();

  const [hoverStatus, setHover] = useState(false);

  const compareSize = () => {
    const compare = ref.current && ref.current.scrollWidth > ref.current.clientWidth;
    setHover(compare);
  };

  useEffect(() => {
    compareSize();
    window.addEventListener('resize', compareSize);
    window.removeEventListener('resize', compareSize);
  }, []);

  // Items marked hideWhenIcmEligible (device-group Instant WiFi) are shown only once the location is known
  // not to be ICM eligible, so an ICM location never flashes them. The lookup only runs for those items, and
  // MenuList starts it as soon as a location or device group is selected; a failed lookup counts as not eligible.
  const icmEligible = useIcmEligibility(Boolean(item?.hideWhenIcmEligible), currentNetwork);
  const isHiddenForIcm = Boolean(item?.hideWhenIcmEligible) && icmEligible !== false;

  const hasFeatureGate = item?.featureGate != null && item?.featureGate !== '';
  // Once a feature list is available (even a cached one), evaluate against it instead of
  // hiding on a transient / failed loading state — a stuck feature-flags fetch must not
  // blank the whole menu.
  const flagsReady = isFeatureListLoaded() || !featureFlagsLoading;
  const isFeatureGateAllowed = hasFeatureGate
    ? flagsReady && isFeatureEnabled(item.featureGate)
    : true;

  // Don't render hidden / feature-gated menu items (must be after all hooks)
  if (item?.hidden || !isFeatureGateAllowed || isHiddenForIcm) {
    return null;
  }

  const Icon = item?.icon;
  const showIcon = item?.hideIcon ? false : true;
  const isFlyoutItem = level > 1; // items inside flyout panel
  const itemIcon = showIcon
    ? item?.icon
      ? (
          <Icon stroke={1.5} size={drawerOpen ? '20px' : '24px'} style={{ ...(isHorizontal && isParents && { fontSize: 20, stroke: '1.5' }) }} />
        )
      : (
          <FiberManualRecordIcon sx={{ width: isSelected ? 8 : 6, height: isSelected ? 8 : 6 }} fontSize={level > 0 ? 'inherit' : 'medium'} />
        )
    : null;

  let itemTarget = '_self';
  if (item.target) {
    itemTarget = '_blank';
  }

  const itemHandler = () => {
    // Close all drawer instances globally (in case drawer is open in a different provider)
    closeAllDrawers();
    // remove this code in phase 1 if it is causing some issue.
    let validateId = (item.id).split("-")[1];
    if (validateId == "parOrg") {
      localStorage.setItem("scopeId", "1")
      setScopeLevel("1")
    } else if (validateId == "mspLevel") {
      localStorage.setItem("scopeId", "0")
      setScopeLevel("0")
    } else if(validateId == "customgroup"){
      localStorage.setItem("scopeId", "3")
      setScopeLevel("3")
    } else {
      localStorage.setItem("scopeId", "2")
      setScopeLevel("2")
    }
    //
    if (downMD) handlerDrawerOpen(false);

    if (isParents && setSelectedID) {
      setSelectedID();
    }
  };

  const trackNavClick = () => {
    recordRumNavClick({
      navScope: getRumNavScope(item.id, itemUrl),
      navId: getRumNavId(item.id, itemUrl),
      featureKey: getRumFeatureKey(itemUrl),
      targetRouteTemplate: getRumPageKey(itemUrl)
    });
  };

  const iconSelectedColor = mode === ThemeMode.DARK && drawerOpen ? 'text.primary' : '';

  /** Sidebar Alarms: open page without notificationPreset so saved filters are not overwritten (bell/dashboard still pass presets). */
  const handleAlarmsClick = (e) => {
    if (e?.preventDefault) e.preventDefault();
    trackNavClick();
    itemHandler();
    navigate(itemUrl);
  };

  return (
    <>
      {!isHorizontal ? (
        <Tooltip disableHoverListener={!item.disabled} title={tooltipMessage ? <FormattedMessage id={tooltipMessage} /> : ''} placement="right" arrow>
          <span>
            <ListItemButton
              data-rum-nav-item="true"
              component={Link}
              to={itemUrl}
              target={itemTarget}
              disabled={item.disabled}
              disableRipple={!drawerOpen}
              sx={{
                zIndex: 1201,
                borderRadius: `${borderRadius}px`,
                mb: 0.5,
                padding: '7px 12px',
                ...(drawerOpen && level !== 1 && {
                  ml: downMD
                    ? level === 2
                      ? '12px'
                      : '24px'
                    : `${level * 18}px`
                }),
                ...(!drawerOpen && { pl: 1.9, }),
                ...(drawerOpen &&
                  level === 1 &&
                  mode !== ThemeMode.DARK && {
                  '&:hover': {
                    bgcolor: 'grey.200',
                  },
                  '&.Mui-selected': {
                    bgcolor: 'secondary.light',
                    color: iconSelectedColor,
                    '&:hover': {
                      color: iconSelectedColor,
                      bgcolor: 'secondary.light'
                    }
                  }
                }),
                ...((!drawerOpen || level !== 1) && {
                  py: level === 1 ? 0 : 0.6,
                  '&:hover': {
                    bgcolor: 'transparent'
                  },
                  '&.Mui-selected': {
                    '&:hover': {
                    },
                  }
                }),
                // When drawer not open, only apply selected styles to level 1 parent items; for child items, keep background transparent even when selected
                ...(!drawerOpen && {
                  '&.Mui-selected': {
                    bgcolor: 'transparent',
                    '&:hover': {
                      bgcolor: 'transparent',
                    }
                  }
                }),
                ...(level !== 1 && !drawerOpen && {
                  '&.Mui-selected': {
                    backgroundColor:
                      mode === ThemeMode.DARK
                        ? alpha(theme.palette.secondary.main, 1)
                        : 'secondary.light'
                  }
                })
              }}
              selected={isSelected}
              onClick={(e) => {
                if (isAlarmsItem) {
                  handleAlarmsClick(e);
                  return;
                }
                trackNavClick();
                itemHandler();
              }}
            >
              <ButtonBase aria-label="theme-icon" sx={{ borderRadius: `${borderRadius}px` }} disableRipple={drawerOpen}>
                <Tooltip title={item.title ? <FormattedMessage id={item.title} /> : ''}
                  placement="right"
                  arrow
                  disableHoverListener={drawerOpen}
                  disableFocusListener={drawerOpen}
                  disableTouchListener={drawerOpen}
                  disableInteractive
                >
                  <ListItemIcon
                    sx={{
                      minWidth: level === 1 ? 32 : 0,
                      pr: level === 1 ? 0 : 0,
                      color: iconSelectedColor,
                      ...(!drawerOpen &&
                        level === 1 && {
                        borderRadius: `${borderRadius}px`,
                        width: 42,
                        height: 42,
                        alignItems: 'center',
                        justifyContent: 'center',
                        '&:hover': {
                          bgcolor: mode === ThemeMode.DARK ? alpha(theme.palette.white.paper, 0.08) : 'grey.200'

                        },
                        ...(isSelected && {
                          bgcolor: mode === ThemeMode.DARK ? alpha(theme.palette.secondary.main, 1) : 'secondary.light',
                          '&:hover': {
                            bgcolor: mode === ThemeMode.DARK ? alpha(theme.palette.secondary.main, 1) : 'secondary.light'
                          }
                        })
                      })
                    }}
                  >
                    {itemIcon}
                  </ListItemIcon>
                </Tooltip>
              </ButtonBase>

              {(drawerOpen || (!drawerOpen && level !== 1)) && (
                <Tooltip title={item.title ? <FormattedMessage id={item.title} /> : ''} disableHoverListener={!hoverStatus}>
                  <ListItemText
                    primary={
                      <Typography
                        id={item.id}
                        ref={ref}
                        noWrap
                        variant='body1'
                        color="inherit"
                        fontWeight={isSelected ? 400 : 400}
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          width: isFlyoutItem ? 200 : 160,
                          ...(item?.id === 'LocLocationSettingsRadius-loc' && { textTransform: 'uppercase' }),
                          transition: (theme) => theme.transitions.create('transform', { duration: theme.transitions.duration.shorter }),
                          ...(themeDirection === ThemeDirection.RTL && { textAlign: 'end', direction: 'rtl' })
                        }}
                      >
                        {item.title ? <FormattedMessage id={item.title} /> : ''}
                      </Typography>
                    }
                    secondary={
                      item.caption && (
                        <Typography variant="caption" id={item.id} gutterBottom sx={{ display: 'block', ...theme.typography.subMenuCaption }}>
                          <FormattedMessage id={item.caption} />
                        </Typography>
                      )
                    }
                  />
                </Tooltip>
              )}

              {drawerOpen && item.chip && (
                <Chip
                  color={item.chip.color}
                  variant={item.chip.variant}
                  size={item.chip.size}
                  label={item.chip.label}
                  avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
                />
              )}
            </ListItemButton>
          </span>
        </Tooltip>
      ) : (
        <ListItemButton
          data-rum-nav-item="true"
          component={Link}
          to={itemUrl}
          target={itemTarget}
          disabled={item.disabled}
          sx={{
            borderRadius: isParents ? `${borderRadius}px` : 0,
            mb: isParents ? 0 : 0.5,
            alignItems: 'flex-start',
            backgroundColor: level > 1 ? 'transparent !important' : 'inherit',
            py: 1,
            pl: 2,
            mr: isParents ? 1 : 0
          }}
          selected={isSelected}
          onClick={(e) => {
            if (isAlarmsItem) {
              handleAlarmsClick(e);
              return;
            }
            trackNavClick();
            itemHandler();
          }}
        >
          <ListItemIcon
            sx={{
              my: 'auto',
              minWidth: !item?.icon ? 18 : 36
            }}
          >
            {itemIcon}
          </ListItemIcon>

          <ListItemText
            sx={{ mb: 0.25 }}
            primary={
              <Typography variant={isSelected ? 'h5' : 'body1'} color="inherit">
                {item.title ? <FormattedMessage id={item.title} /> : ''}
              </Typography>
            }
            secondary={
              item.caption && (
                <Typography variant="caption" gutterBottom sx={{ display: 'block', ...theme.typography.subMenuCaption }}>
                  {item.caption}
                </Typography>
              )
            }
          />

          {item.chip && (
            <Chip
              color={item.chip.color}
              variant={item.chip.variant}
              size={item.chip.size}
              label={item.chip.label}
              avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
            />
          )}
        </ListItemButton>
      )}
    </>
  );
}

NavItem.propTypes = { item: PropTypes.any, level: PropTypes.number, isParents: PropTypes.bool, setSelectedID: PropTypes.func };
